import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Package, FileText, Loader2, CheckCircle2, TruckIcon } from 'lucide-react';

const MaterialRequest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    request_type: 'tags',
    quantity_tags: 0,
    quantity_brochures: 0,
    delivery_notes: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.request_type === 'tags' && formData.quantity_tags === 0) {
      toast.error('Please specify quantity for pet tags');
      return;
    }
    if (formData.request_type === 'brochures' && formData.quantity_brochures === 0) {
      toast.error('Please specify quantity for brochures');
      return;
    }
    if (formData.request_type === 'both' && formData.quantity_tags === 0 && formData.quantity_brochures === 0) {
      toast.error('Please specify at least one quantity');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('vet_portal_token');
      const rawResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/material-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      if (rawResponse.status === 401) {
        localStorage.removeItem('vet_portal_token');
        localStorage.removeItem('vet_portal_clinic');
        localStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return;
      }
      const response = rawResponse.clone();

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Request failed');
      }

      const data = await response.json();
      toast.success('Material request submitted successfully!', {
        description: `Request ID: ${data.request_id || 'N/A'}`
      });
      
      // Navigate to history
      setTimeout(() => navigate('/material-history'), 1500);
      
    } catch (error) {
      toast.error('Failed to submit request', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8" data-testid="material-request-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold mb-2 text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Request Marketing Materials
          </h1>
          <p className="text-muted-foreground text-lg">
            Order pet tags and brochures for your clinic
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <Card className="border-0 shadow-md hover:shadow-lg transition-shadow duration-300">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Package className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Fredoka, sans-serif' }}>Material Request Form</CardTitle>
                  <CardDescription>
                    Select the materials you need and specify quantities
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Request Type */}
              <div className="space-y-3">
                <Label>Request Type *</Label>
                <RadioGroup
                  value={formData.request_type}
                  onValueChange={(value) => setFormData({ ...formData, request_type: value })}
                  data-testid="request-type-radio"
                >
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="tags" id="tags" />
                    <Label htmlFor="tags" className="flex items-center cursor-pointer flex-1">
                      <Package className="h-5 w-5 mr-2 text-primary" />
                      <span>Pet Tags Only (30, 50, or 80 tags)</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="brochures" id="brochures" />
                    <Label htmlFor="brochures" className="flex items-center cursor-pointer flex-1">
                      <FileText className="h-5 w-5 mr-2 text-primary" />
                      <span>Brochures Only (50-250 brochures)</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-accent cursor-pointer">
                    <RadioGroupItem value="both" id="both" />
                    <Label htmlFor="both" className="flex items-center cursor-pointer flex-1">
                      <div className="flex items-center">
                        <Package className="h-5 w-5 mr-1 text-primary" />
                        <FileText className="h-5 w-5 mr-2 text-primary" />
                      </div>
                      <span>Both Tags and Brochures</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Quantity Tags */}
              {(formData.request_type === 'tags' || formData.request_type === 'both') && (
                <div className="space-y-2">
                  <Label htmlFor="quantity_tags">Quantity of Pet Tags *</Label>
                  <Select
                    value={formData.quantity_tags.toString()}
                    onValueChange={(value) => setFormData({ ...formData, quantity_tags: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="quantity-tags-select">
                      <SelectValue placeholder="Select quantity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 tags</SelectItem>
                      <SelectItem value="50">50 tags</SelectItem>
                      <SelectItem value="80">80 tags</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">Choose from preset quantities</p>
                </div>
              )}

              {/* Quantity Brochures */}
              {(formData.request_type === 'brochures' || formData.request_type === 'both') && (
                <div className="space-y-2">
                  <Label htmlFor="quantity_brochures">Quantity of Brochures *</Label>
                  <Select
                    value={formData.quantity_brochures.toString()}
                    onValueChange={(value) => setFormData({ ...formData, quantity_brochures: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="quantity-brochures-select">
                      <SelectValue placeholder="Select quantity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50 brochures</SelectItem>
                      <SelectItem value="80">80 brochures</SelectItem>
                      <SelectItem value="100">100 brochures</SelectItem>
                      <SelectItem value="150">150 brochures</SelectItem>
                      <SelectItem value="200">200 brochures</SelectItem>
                      <SelectItem value="250">250 brochures</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">Choose from preset quantities</p>
                </div>
              )}

              {/* Delivery Notes */}
              <div className="space-y-2">
                <Label htmlFor="delivery_notes">Delivery Notes (Optional)</Label>
                <Textarea
                  id="delivery_notes"
                  value={formData.delivery_notes}
                  onChange={(e) => setFormData({ ...formData, delivery_notes: e.target.value })}
                  placeholder="Any special delivery instructions..."
                  rows={3}
                  data-testid="delivery-notes-textarea"
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Submit Button */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 h-12 text-base font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  data-testid="submit-material-request-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting Request...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-5 w-5" />
                      Submit Request
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  disabled={loading}
                  className="h-12"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        </motion.div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-0 shadow-md">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/20\">
                  <TruckIcon className="h-5 w-5 text-primary" />
                </div>
                <CardTitle className="text-lg" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  Important Information
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>• You'll receive an email confirmation once your request is submitted</p>
              <p>• Materials typically arrive within 3 business days</p>
              <p>• For urgent requests, please contact us directly</p>
              <p>• Available quantities: Tags (30, 50, 80) | Brochures (50, 80, 100, 150, 200, 250)</p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
};

export default MaterialRequest;
